<template>
	<div class="chartWrapper chartWrapper--2022" ref="chart2022">

		<!-- To select round and runoff -->
		<tabs-group :mode="mode" :rounds="rounds" :runoffs="runoffs" :mobile="mobile" @change="changeChart($event)" />

		<!-- To change timespan -->
		<radios class="listoptions listoptions--timespan" v-if="mode == 'full' && !mobile" :selected-option="activeTimespan" :options="timespans" @change="activeTimespan = $event" :align="'flex'" :width="'auto'" />

		<div class="chartContainer">

			<!-- Candidates toggles -->
			<div v-if="!mobile && mode == 'full'" class="candidatesWrapper" >
				<div class="candidates">
					<presi-candidate v-for="candidate in listedCandidates" :key="candidate.key" :candidate="candidate" :active="activeCandidatesId.includes(candidate.id)" @select="toggleCandidate($event)" :type="'toggle'" :width="160" />

					<div v-for="i in 5" :key="'candidatefiller-' + i" class="candidate --filler" :style="{flexBasis: '160px' }"></div>
				</div>

				<!-- Show/hide former candidates -->
				<checkboxes class="listoptions listoptions--candidates" v-if="mode == 'full' && !mobile && activeRound.code == 'round1' "  :options="showQuittersOptions" @change="showQuittersOptions[0].checked = !showQuittersOptions[0].checked" :width="'auto'" />	
			</div>
			
			<!-- Line chart -->
			<linechart :comments="chartData.comments" :id="'linechart-firstRound-parties'" :show-info-window="mode == 'full' ? true : false" :mobile="mobile" :line-width="chartSettings.lineWidth" :size="chartSettings.size" :margin="chartSettings.margin" :raw-dates="chartData.dates" :lines="chartData.lines" :dots="chartData.dots" :date-format="dateFormat" :dot-size="chartSettings.dotSize" :y-type="chartData.yType" :y-unit="chartData.yUnit" :show-cursor-line="true" :areas="chartData.areas" />

		</div>
		
		<!-- Data attribution -->
		<div class="attribution">
			<div class="attribution__text">
				<p><strong>Données&nbsp;:</strong> notices envoyées à <a :href="pollCommittee.url" target="_blank">{{pollCommittee.name}}</a>, mises en forme par Contexte après compilation par <a :href="nspPolls.url" target="_blank">{{nspPolls.name}}</a>.</p>
			</div>
			<div v-if="!mobile" class="attribution__image">
				<a :href="nspPolls.url" target="_blank">
					<img :src="nspPolls.logo" alt="NSP Polls">
				</a>
			</div>
		</div>

		<!-- Methodology -->
		<div class="method">
			<div class="method__text">
				<p><strong>Méthode&nbsp;:</strong> pour chaque candidat, la courbe de tendance est obtenue par <a :href="wikipedia.localRegression.url" target="_blank">régression locale</a> de tous les scores obtenus dans les hypothèses testées par les sondeurs. <template v-if="(this.mode != 'light' || !this.candidatesInUrl.length) && this.minCandidateScore > 0">Seuls les candidats dont le score agrégé le plus récent dépasse {{this.minCandidateScoreTxt}}&nbsp;% sont affichés.</template></p>
				<template v-if="this.mode != 'light' || !this.candidatesInUrl.length">
					<p><strong>Changement le 7 février :</strong> le sondeur Cluster 17 ayant fait l’objet de deux mises en garde de la Commission des sondages (<a :href="pollCommittee.warnings[0]" target="_blank">le 8&nbsp;janvier</a> et <a :href="pollCommittee.warnings[1]" target="_blank">le 4&nbsp;février</a>), ses résultats ne sont plus inclus dans le calcul des tendances.</p>
				</template>
			</div>
		</div>


		<!-- Candidates selector on mobile -->
		<toggles v-show="mobile && activeRound.code == 'round1' && mode != 'light' && showMobileToggles" :active-candidates-id="activeCandidatesId" :candidates="listedCandidates" ref="toggles" @change="toggleCandidate($event)"/>

	</div>
</template>
<script>
	import { clone, normalize, toLocale, inIframe } from 'global/js/apps.js'
	import Checkboxes from 'global/components/Checkboxes'
	import Radios from 'global/components/Radios'
	import PresiCandidate from 'global/components/PresiCandidate'
	import TabsGroup from '../components/TabsGroup'
	import Linechart from 'global/components/Linechart'
	import nspPollsLogo from '../assets/nsp_polls.png' 
	import Toggles from '../components/Toggles'

	const nspPollsUrl = 'https://github.com/nsppolls/nsppolls'
	const pollCommitteeUrl = 'https://www.commission-des-sondages.fr/notices/'
	const localRegressionUrl = 'https://fr.wikipedia.org/wiki/R%C3%A9gression_locale'
	const pollCommitteeC17Warning1 = 'https://www.commission-des-sondages.fr/hist/communiques/communique-sondages-elections-presidentielles-8-Janvier-2022.htm'
	const pollCommitteeC17Warning2 = 'https://www.commission-des-sondages.fr/hist/communiques/mise-au-point-sondages-elections-presidentielles-4-fevrier-2022.htm'


	export default {
		name: 'Chart2022',
		components: { Checkboxes, Radios, PresiCandidate, Linechart, TabsGroup, Toggles },
		props: {
			allCandidates: {
				type: Array,
				required: true,
			},
			allChartsData: {
				type: Array,
				required: true,
			},
			mobile: {
				type: Boolean,
				default: false,
			},
			largeScreen: {
				type: Boolean,
				default: false,
			},
			mode: {
				type: String,
				default: 'full',
			},
			runoffs: {
				type: Object,
				required: true,
			},
			defaultCandidatesNb: {
				type: Number,
				default: 10,
			},
			minCandidateScore: {
				type: Number,
				default: 2,
			},
			scrollY: {
				type: Number,
				default: 0,
			},
			isDev: {
				type: Boolean,
				default: false,
			},
			candidatesInUrl: {
				type: Array,
				default: () => [],
			}
		},
		data: function() {
			return {
				nspPolls: {
					logo: nspPollsLogo,
					url: nspPollsUrl,
					name: 'NSPPolls',
				},
				pollCommittee: {
					url: pollCommitteeUrl,
					name: 'la Commission des sondages',
					warnings: [ pollCommitteeC17Warning1, pollCommitteeC17Warning2 ]
				},
				wikipedia: {
					localRegression: {
						url: localRegressionUrl,
					}
				},
				rwdChartSettings: {
					mobile: {
						margin: { top: 80, right: 162, bottom: 40, left: 75, },
						size: {	width: 800,	height: 1000, },
						lineWidth: 5,
						dotSize: 2.5,
					},
					desktop: {
						margin: { top: 40, right: 85, bottom: 40, left: 43, },
						size: {	width: 800 , height: 600,},
						lineWidth: 1.5,
						dotSize: 1.5,
					},
					largeScreen: {
						margin: { top: 40, right: 85, bottom: 40, left: 43, },
						size: {	width: 800 , height: 700,},
						lineWidth: 1.5,
						dotSize: 2,
					},
				},
				showMobileToggles: true,
				activeCandidatesId: [],
				activeTimespan: 'three-months',
				timespans: [
					{
						label: 'Depuis un an',
						code: 'a-year',
					},
					{
						label: 'Depuis trois mois',
						code: 'three-months',
					},
				],
				activeRound: null,
				rounds: {
					default: 'round2',
					list: [
						{
							code: 'round1',
							inactive: false,
							wording: 'Premier tour',
						},
						{
							code: 'round2',
							inactive: false,
							wording: 'Deuxième tour',
						},
					]
				},
				activeRunoff: null,
				showQuittersOptions: [
					{
						label: 'Voir les ex-candidats',
						code: 'show-quitters',
						checked: false,
					}
				],
			}
		},
		computed: {
			minCandidateScoreTxt: function() {
				return toLocale(this.minCandidateScore, 2)
			},
			candidates: function() {
				return this.allCandidates.filter(c => this.activeCandidatesId.includes(c.id))
			},
			listedCandidates: function() {
				return this.allCandidates.reduce((acc, candidate) => {
					if (this.activeRound.code == 'round1' && (!this.showQuittersOptions[0].checked && candidate.quitter == 'oui')) return acc
					if (this.activeRound.code == 'round2' && !this.activeRunoff.code.includes(normalize(candidate.full_name, true))) return acc

					candidate.key = candidate.id + this.activeRound.code
					if (this.activeRound.code == 'round2') candidate.key += this.activeRunoff.code

					candidate.lastTxt = ''
					let thisArea = this.chartData.areas.find(a => a.label == candidate.last_name)
					if (!!thisArea) {
						let last = thisArea.data[thisArea.data.length - 1]
						if (!last) {
							let thisDots = this.chartData.dots.filter(d => d.label == candidate.last_name)
							last = thisDots[0].y
							candidate.lastTxt = toLocale(last, 2) + '\xa0%'
						} else {
							candidate.lastTxt = toLocale(last.min, 2) + '–' + toLocale(last.max, 2) + '\xa0%'
						}
					}

					return acc.concat(candidate)
				},[])
			},
			chartData: function() {
				let data = {
					yType: 'Intentions de vote',
					yUnit: '%',
					lines: [],
					dots: [],
					areas: [],
					dates: [],
					comments: [],
				}
				let thisChart = null
				let roundCharts = this.allChartsData.filter(c => c.round == this.activeRound.code && c.timespan == this.activeTimespan)
				if (this.activeRound.code == 'round2') {
					thisChart = roundCharts.find(c => c.code == this.activeRunoff.code)
				} else {
					thisChart = roundCharts[0]
				}

				if (!thisChart) return data
				data.dates = [
					thisChart.start,
					thisChart.end
				]
				this.candidates.map(candidate => {
					let thisChartCandidate = thisChart.candidates.find(c => c.full_name == candidate.full_name)
					if (!!thisChartCandidate) {
						if (!!thisChartCandidate.line) data.lines.push(thisChartCandidate.line)
						if (!!thisChartCandidate.area) data.areas.push(thisChartCandidate.area)
						data.dots = data.dots.concat(thisChartCandidate.dots)
					}
				})

				return data
			},
			dateFormat: function() {
				if (this.timespan == 'a-year') return  '%b %Y'
				return '%e %b'
			},
			chartSettings: function() {
				let allSettings = clone(this.rwdChartSettings)
				let settings = allSettings.desktop	
				if (this.mobile) settings = allSettings.mobile
				if (this.largeScreen) settings = allSettings.largeScreen
				if (this.timespan == 'three-months') settings.dotSize *= 1.5

				return settings
			},
		},
		methods: {
			toggleCandidate: function(id) {
				if (this.activeRound.code =='round1') {
					if (this.activeCandidatesId.includes(id)) {
						this.activeCandidatesId = this.activeCandidatesId.filter(c => c != id)
					} else {
						this.activeCandidatesId.push(id)
					}
				}				
			},
			changeChart: function(change) {
				if (change.type == 'round') this.activeRound = this.rounds.list.find(r => r.code == change.value)
				if (change.type == 'runoff') this.activeRunoff = this.runoffs.list.find(r => r.code == change.value)
				this.initiateActiveCandidates()
			},
			initiateActiveCandidates: function() {
				// console.log('initiate candidate list')
				this.activeCandidatesId = []
				this.listedCandidates.forEach((candidate, c) => {
					if (this.activeRound.code == 'round2' || this.mode == 'light' || c+1 <= this.defaultCandidatesNb) this.activeCandidatesId.push(candidate.id)
				})		
			}, 
		},
		created() {
			this.activeRound = this.rounds.list.find(r => r.code == this.rounds.default)
			this.activeRunoff = this.runoffs.list.find(r => r.code == this.runoffs.default)
			if (this.mode == 'light') {
				this.rwdChartSettings.desktop.dotSize = 2
			}
			this.initiateActiveCandidates()
		},
		mounted() {
			if (inIframe() && this.mobile && this.mode == 'full') {
				this.$nextTick(function() {
					let $iframe = parent.document.querySelector('body > iframe')
					if (this.isDev) $iframe = parent.document.querySelector('body iframe')
					let $target = $iframe.parentNode
					$target.appendChild(this.$refs.toggles.$el)
				})
			}
		},
		watch: {
			scrollY: function() {
				if (inIframe() && this.mobile && this.mode == 'full') {
					if (!!this.$refs.chart2022) {
						let bottomChart = this.$refs.chart2022.getBoundingClientRect().bottom + document.body.scrollTop
						bottomChart < -50 ? this.showMobileToggles = false : this.showMobileToggles = true
					}
				}
			},
		}
	}
</script>
<style lang="scss">
	.chartWrapper.chartWrapper--2022 {
		position: relative;
		.chart {
			&Container {
				display: flex;
				flex-wrap: wrap;
				> div {
					margin-bottom: 2em;
				}
			}
			&--linechart {		
				flex: 1 1 500px;
				padding-left: 1.5em;
				margin-top: -.7em;
			}
		}
	}

	.candidates {
		&Wrapper {
			flex: 0 0 130px;
		}
		display: flex;
		flex-wrap: wrap;
		align-content: flex-start;
		width: calc(100% + 14px);
		margin: 0 -7px 1em;
	}
	.listoptions {
		margin-bottom: 1.7em;
		&--candidates {
			.heading{
				font-size: .9em;
			}
			.option {
				font-size: .7em;
			}
		}
		&--timespan {
			position: absolute;
			right: 0;
			top: .5em;
			.option {
				font-size: .8em;
			}
		}
	}
	@media screen and (min-width: 1300px)  {
		.candidates {
			&Wrapper {
				flex-basis: 205px;
			}
		}
	}
	@media screen and (max-width: 900px)  {
		.chartWrapper.chartWrapper--2022 {
			.chart {
				padding-top: 0;
				position: relative;
				padding-left: 1px;
			}
		}
		.candidates {
			&Wrapper {
				flex: 0 0 100%;
				margin-bottom: 2em;
			}
			margin: 0 -5px;
		}
	}

</style>